import { useCallback, useMemo, useState } from "react"

import { useParams } from "react-router-dom"

import { API_MODULE_DOCUMENT, DocumentEditModal } from "@l2r-front/l2r-app-doc"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, EditLocationIcon, NoteAddRoundedIcon } from "@l2r-front/l2r-icons"
import { useNetworksStateContext, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { MoreMenu } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { STATUS_DONE } from "../../constants/status"
import { useRoadworksDispatchContext } from "../../contexts/RoadworksContext"
import { RoadworkAddDocumentForm } from "../../forms/RoadworkAddDocumentForm"
import { useRoadworkDelete } from "../../hooks/mutations/roadwork/useRoadworkDelete"
import { useRoadworkUpdate } from "../../hooks/mutations/roadwork/useRoadworkUpdate"
import { useRoadworkWithTasks } from "../../hooks/queries/useRoadwork"

import { RoadworkSectionPainter } from "../RoadworkSectionPainter"
import { RoadworkSectionSidebarContent } from "../RoadworkSectionSidebarContent"

import * as Styled from "./RoadworkSectionSidebar.styled"

export function RoadworkSectionSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigateWithSearchParams()
    const { road, roadworkUuid } = useParams()
    const [documentAddModalOpen, setDocumentAddModalOpen] = useState(false)
    const readOnly = useRoleIsReadOnly()
    const { setSelectedRoadwork } = useRoadworksDispatchContext()
    const [editLocationModalOpen, setEditLocationModalOpen] = useState(false)
    const { selectedNetwork } = useNetworksStateContext()
    const { setAlert } = useAlertsDispatchContext()

    const navigateBack = useCallback(() => {
        navigate(`../${road}`, { navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [navigate, road])

    const { data: roadwork } = useRoadworkWithTasks(roadworkUuid, road)

    const sidebarTitle = useMemo(() => {
        if (roadwork && !roadwork.tasks.length) {
            return t(I18N_NAMESPACE, "containers.roadworkSectionSidebar.titleCreate")
        }
        return t(I18N_NAMESPACE, "containers.roadworkSectionSidebar.title")
    }, [roadwork, t])

    const isEditable = useMemo(() => {
        const currentYear = new Date().getFullYear()
        return roadwork && roadwork.year >= currentYear && roadwork.status !== STATUS_DONE && !readOnly
    }, [roadwork, readOnly])

    const isDeletable = useMemo(() => {
        return roadwork && roadwork.status !== STATUS_DONE && !readOnly
    }, [roadwork, readOnly])

    const deleteMutation = useRoadworkDelete(roadwork, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteError"),
        onSuccess: () => {
            navigate(`../${road}`)
            setSelectedRoadwork(null, false)
        },
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkCard.snackbar.deleteSuccess"),
    })

    const mutation = useRoadworkUpdate(roadwork, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkDetail.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.roadworkDetail.snackbar.success"),
    })

    const closeModal = useCallback(() => {
        setEditLocationModalOpen(false)
    }, [])

    const onSave = useCallback((data) => {
        if (data) {
            mutation.mutate(data)
        }
        closeModal()
    }, [mutation, closeModal])

    return (
        <>
            <Styled.BottomSheetContent
                id="works-technique-sidebar"
                onBack={navigateBack}
                title={sidebarTitle}
                titleSideComponent={<MoreMenu
                    menuConfig={[
                        {
                            enabled: !!isEditable,
                            id: "edit-location",
                            label: t(I18N_NAMESPACE, "containers.roadworkSection.edit.location"),
                            icon: <EditLocationIcon color="objects/object-black" />,
                            props: {
                                onClick: () => setEditLocationModalOpen(true),
                            },
                        },
                        {
                            enabled: !!(selectedNetwork.modules.includes(API_MODULE_DOCUMENT) && !readOnly),
                            id: "add-document",
                            label: t(I18N_NAMESPACE, "containers.roadworkSection.addDocument"),
                            icon: <NoteAddRoundedIcon color="objects/object-black" />,
                            props: {
                                onClick: () => setDocumentAddModalOpen(true),
                            },
                        },
                        {
                            enabled: !!(isDeletable && roadwork.tasks.length),
                            id: "delete-roadwork",
                            label: t(I18N_NAMESPACE, "containers.techniqueItemCard.menu.delete"),
                            icon: <DeleteIcon color="objects/object-black" />,
                            props: {
                                onClick: () => {
                                    setAlert({
                                        alertTitle: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirmation"),
                                        cancelText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.cancel"),
                                        confirmText: t(I18N_NAMESPACE, "containers.techniqueItemCard.delete.confirm"),
                                        callback: deleteMutation.mutate,
                                        severity: "warning",
                                    })
                                },
                            },
                        },
                    ]}
                />}
            >
                <RoadworkSectionPainter
                    open={editLocationModalOpen}
                    onClose={closeModal}
                    onSave={onSave}
                    title={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.update.title")}
                    desc={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.update.desc")}
                    cancelButtonLabel={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.update.cancel")}
                    saveButtonLabel={t(I18N_NAMESPACE, "components.roadworkSectionPainterModal.update.save")}
                />
                <RoadworkSectionSidebarContent />
            </Styled.BottomSheetContent >
            {documentAddModalOpen && <DocumentEditModal
                open={documentAddModalOpen}
                onClose={() => setDocumentAddModalOpen(false)}
                FormComponent={RoadworkAddDocumentForm}
                roadwork={roadwork}
            />}
        </>
    )
}
